import React, { useState, useEffect } from 'react';
import { RiTruckLine, RiCalendarLine, RiCloseLine, RiAlertLine } from 'react-icons/ri';
import Modal from '../ui/Modal';
import { showAlert } from '../../utils/alert';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { equipmentReturnService } from '../../services/equipmentReturnService';
import { Rental } from '../../services/rentalService';
import { ReturnItemIdentity } from './ReturnItemIdentity';
import { formatDate } from '../../utils/dateUtils';

interface ContractEndReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  rental: Rental;
  onSuccess?: () => void;
}

const ContractEndReturnModal: React.FC<ContractEndReturnModalProps> = ({
  isOpen,
  onClose,
  rental,
  onSuccess
}) => {
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Set default scheduled date to rental end_date or expected_return_date
  useEffect(() => {
    if (isOpen && rental) {
      const defaultDate = rental.end_date || rental.expected_return_date;
      if (defaultDate) {
        setScheduledDate(defaultDate.split('T')[0]); // Format as YYYY-MM-DD
      }
    }
  }, [isOpen, rental]);

  const handleReset = () => {
    const defaultDate = rental.end_date || rental.expected_return_date;
    if (defaultDate) {
      setScheduledDate(defaultDate.split('T')[0]);
    } else {
      setScheduledDate('');
    }
    setNotes('');
    setErrors({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!scheduledDate) {
      newErrors.scheduledDate = 'กรุณาระบุวันที่นัดคืน';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showAlert('ข้อมูลไม่ครบถ้วน', 'กรุณากรอกข้อมูลให้ครบถ้วน', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      await equipmentReturnService.createFromContractEnd({
        rental_id: rental.id,
        scheduled_date: scheduledDate,
        notes: notes.trim() || undefined
      });

      showAlert('สำเร็จ', 'สร้างรายการขอคืนอุปกรณ์แล้ว', 'success');
      handleReset();
      onClose();
      onSuccess?.();
    } catch (error: any) {
      console.error('Error creating contract end return:', error);
      const errorMessage = getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการสร้างรายการ');
      showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!rental) return null;

  const endDate = rental.end_date || rental.expected_return_date;
  const rentalItems = rental.rental_items || [];

  // 🔴 บั๊กเดิม: นับและกรองด้วย `item.device_id` อย่างเดียว
  //    → รายการเช่าที่มี "แต่ซิม" (ไม่มีเครื่อง) หายไปทั้งใบ ขึ้นว่า "ไม่พบอุปกรณ์ในรายการเช่านี้"
  //      และปุ่ม "สร้างรายการคืน" ถูก disable ด้วย `deviceCount === 0`
  //      = สัญญาที่มีแต่ซิม เปิดใบตามเก็บจากหน้านี้ไม่ได้เลย
  //    (backend `/from-contract-end` สร้างใบให้ทุกรายการที่มี device_id **หรือ** sim_id อยู่แล้ว
  //     จอจึงแสดงน้อยกว่าที่ระบบจะสร้างจริง = ผู้ใช้เซ็นใจโดยไม่เห็นของครบ)
  //
  // ตั้งแต่แยกแถว: 1 รายการเช่าที่มีทั้งเครื่องและซิม จะกลายเป็นใบตามเก็บ 2 แถว
  // จึงต้องพรีวิวเป็น "ชิ้น" ให้ตรงกับสิ่งที่จะถูกสร้างจริง
  const returnableItems = rentalItems.filter(item => item.device_id || item.sim_id);
  const previewPieces = returnableItems.flatMap(item => {
    const pieces: Array<{ key: string; item: typeof item; kind: 'device' | 'sim' }> = [];
    if (item.device_id) pieces.push({ key: `d-${item.id}-${item.device_id}`, item, kind: 'device' });
    if (item.sim_id) pieces.push({ key: `s-${item.id}-${item.sim_id}`, item, kind: 'sim' });
    return pieces;
  });
  const pieceCount = previewPieces.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center">
          <RiTruckLine className="w-6 h-6 text-amber-600 mr-2" />
          ขอคืนอุปกรณ์ (สิ้นสุดสัญญา)
        </div>
      }
      size="lg"
    >
      <div className="space-y-6">
        {/* ข้อมูลการเช่า */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-sm">
                <RiAlertLine className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-semibold text-amber-900 mb-2">ข้อมูลการเช่า</h4>
              <div className="text-sm text-amber-800 space-y-1">
                <p><strong>รหัสการเช่า:</strong> {rental.rental_number}</p>
                <p><strong>บริษัท:</strong> {rental.company?.name || '-'}</p>
                {rental.site && <p><strong>โครงการ:</strong> {rental.site.name}</p>}
                <p><strong>วันหมดสัญญา:</strong> {endDate ? formatDate(endDate) : '-'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* รายการอุปกรณ์ที่ต้องคืน */}
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-3">
            <div className="w-8 h-8 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg flex items-center justify-center shadow-sm">
              <RiTruckLine className="w-4 h-4 text-white" />
            </div>
            <h4 className="text-base font-semibold text-secondary-800">
              ของที่ต้องเก็บคืน ({pieceCount} ชิ้น)
            </h4>
          </div>

          {pieceCount > 0 ? (
            <>
              <p className="text-xs text-secondary-500 mb-2">
                เครื่องและซิมเป็นคนละรายการ — ระบบจะสร้างใบตามเก็บแยกชิ้น
                เพื่อให้บันทึกได้ว่าได้เครื่องคืนแต่ซิมหาย (หรือกลับกัน)
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {previewPieces.map(({ key, item, kind }) => (
                  <div key={key} className="bg-white p-3 rounded border border-secondary-200">
                    {kind === 'device' ? (
                      <ReturnItemIdentity
                        item={{
                          device_id: item.device_id,
                          device: {
                            code: item.device?.code,
                            serial_number: item.device?.serial_number,
                            model: item.device?.model || item.product?.name,
                          },
                        }}
                      />
                    ) : (
                      <ReturnItemIdentity
                        item={{
                          sim_id: item.sim_id,
                          sim: {
                            phone_number: item.sim?.phone_number,
                            sim_number: item.sim?.sim_number,
                          },
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-secondary-500 text-sm text-center py-4">
              ไม่พบเครื่องหรือซิมที่ต้องเก็บคืนในรายการเช่านี้
            </p>
          )}
        </div>

        {/* ฟอร์มกรอกข้อมูล */}
        <div className="space-y-4">
          {/* วันที่นัดคืน */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              วันที่นัดคืน <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => {
                  setScheduledDate(e.target.value);
                  setErrors(prev => ({ ...prev, scheduledDate: '' }));
                }}
                className={`input pl-10${errors.scheduledDate ? ' border-red-500' : ''}`}
                aria-invalid={Boolean(errors.scheduledDate)}
                aria-describedby={errors.scheduledDate ? 'scheduledDate-error' : undefined}
              />
            </div>
            {errors.scheduledDate && (
              <p id="scheduledDate-error" className="mt-1 text-sm text-red-600">{errors.scheduledDate}</p>
            )}
          </div>

          {/* หมายเหตุ */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              หมายเหตุ
            </label>
            <textarea
              aria-label="หมายเหตุ"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="เพิ่มหมายเหตุเกี่ยวกับการคืนอุปกรณ์ (ถ้ามี)"
              className="input"
              maxLength={2000}
            />
            <p className="mt-1 text-xs text-secondary-500 text-right">
              {notes.length}/2000
            </p>
          </div>
        </div>

        {/* ปุ่มดำเนินการ */}
        <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ยกเลิก
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || pieceCount === 0}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            {isSubmitting ? 'กำลังสร้าง...' : 'สร้างรายการคืน'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ContractEndReturnModal;
